'use client';

import React, { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import {
  type MapboxDirectionsProfile,
  type ProfileConfig,
  profiles,
} from '@/app/components/hotel-detail/MapRouteProfileSwitch';

export type Place = {
  id: string;
  coordinates: LatLng;
  text: string;
  category?: string;
};

export type AirportToDestinationItinerary = {
  airport: {
    coordinates: LatLng;
    iata: string;
    name: string;
  };
  destination: {
    coordinates?: LatLng;
    name: string;
    rating: number;
  };
  distance: string;
};

type ShowMapOptions = {
  airportToDestinationItinerary?: AirportToDestinationItinerary;
};

export type PlaceWithDirections = {
  id: string;
  coordinates: LatLng;
  text: string;
  directions?: {
    distance: string;
    duration: string;
    profile: MapboxDirectionsProfile;
  };
  category?: string;
  wikidata?: string;
  rank?: number;
  isFromLayer?: boolean;
};

type HotelMapModalContextProps = {
  airportToDestinationItinerary?: AirportToDestinationItinerary;
  closeMap: () => void;
  isShowingMap: boolean | undefined;
  poiMarkerSelected?: PlaceWithDirections;
  selectedProfile: ProfileConfig;
  setPoiMarkerSelected: Dispatch<SetStateAction<Place | undefined>>;
  setSelectedProfile: Dispatch<SetStateAction<ProfileConfig>>;
  showMap: (options?: ShowMapOptions) => void;
};

type HotelMapModalProviderProps = {
  children: React.ReactNode;
};

const defaultProfile = profiles.driving;

export const HotelMapModalContext = React.createContext<HotelMapModalContextProps | undefined>(undefined);

export default function HotelMapModalProvider(props: HotelMapModalProviderProps) {
  const { children } = props;
  const [isShowingMap, setIsShowingMap] = React.useState<boolean | undefined>(undefined);
  const [airportToDestinationItinerary, setAirportToDestinationItinerary] = React.useState<
    AirportToDestinationItinerary | undefined
  >(undefined);
  const [selectedProfile, setSelectedProfile] = useState<ProfileConfig>(defaultProfile);
  const [poiMarkerSelected, setPoiMarkerSelected] = useState<PlaceWithDirections | undefined>();
  // On iPhone safari software keyboard will push the whole page this returns the user to the same scroll point
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  function showMap(options?: ShowMapOptions) {
    setIsShowingMap(true);
    setScrollPosition(window.scrollY);
    setAirportToDestinationItinerary(options?.airportToDestinationItinerary);
  }

  function closeMap() {
    setIsShowingMap(false);
    window.scroll(0, scrollPosition);
  }

  const memoizedValue = useMemo(
    () => ({
      airportToDestinationItinerary,
      closeMap,
      isShowingMap,
      poiMarkerSelected,
      selectedProfile,
      setPoiMarkerSelected,
      setSelectedProfile,
      showMap,
    }),
    [airportToDestinationItinerary, isShowingMap, poiMarkerSelected, selectedProfile]
  );

  return <HotelMapModalContext.Provider value={memoizedValue}>{children}</HotelMapModalContext.Provider>;
}

export function useHotelMapModal() {
  const context = React.useContext(HotelMapModalContext);

  if (context === undefined) {
    throw new Error('useHotelMapModal must be within a HotelMapProvider');
  }

  return context;
}
